import React, { useState, useRef, useEffect } from "react";
import { IoIosSearch, IoMdSave } from "react-icons/io";
import SaveSearchModal from "./SaveSearchModal";
import api from "../../api/apiClient";

export default function TagInputSearch({ handleChange, tags }) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [term, setTerm] = useState("");
  const [searchHistory, setSearchHistory] = useState(() => {
    const savedHistory = localStorage.getItem("searchHistory");
    return savedHistory ? JSON.parse(savedHistory) : [];
  });
  const [showAllSuggestions, setShowAllSuggestions] = useState(false); // Track if all suggestions should be shown
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("searchHistory", JSON.stringify(searchHistory));
  }, [searchHistory]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, [tags]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.value.trim() !== "") {
      const newTag = e.target.value.trim();
      const newTags = [...tags, newTag];
      handleChange(newTags);

      setSearchHistory((prevHistory) => {
        const updatedHistory = [newTag, ...prevHistory];
        return [...new Set(updatedHistory)];
      });

      e.target.value = "";
    } else if (
      e.key === "Backspace" &&
      e.target.value === "" &&
      tags.length > 0
    ) {
      handleChange(tags.slice(0, -1));
    }
  };

  const removeTag = (indexToRemove) => {
    handleChange(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleCheckboxChange = async () => {
    setIsChecked(!isChecked);
    const baseUrl =
      "https://mattersmith6.embeddedexperience.com/thesaurus/similar_terms";

    try {
      const url = new URL(baseUrl);
      url.searchParams.append("retina_name", "en_synonymous");
      url.searchParams.append("term", term);
      url.searchParams.append("start_index", "0");
      url.searchParams.append("max_results", "30");
      url.searchParams.append("get_fingerprint", "false");

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tags }),
      });

      if (!response.ok) throw new Error("Thesaurus API call failed");

      const data = await response.json();
    } catch (error) {
      console.error("Error:", error.message);
    }
  };

  const handleSave = async () => {
    try {
      const response = await api.post("/save-search", {
        name: searchName,
        tags,
      });
      if (response.status !== 200)
        throw new Error("Save search API call failed");

      setShowModal(false);
      setSearchName("");
    } catch (error) {
      if (error.response) {
        console.error("Error response from server:", error.response.data);
      } else if (error.request) {
        console.error(
          "No response received, possible network/CORS issue:",
          error.request
        );
      } else {
        console.error("Error setting up the request:", error.message);
      }
    }
  };

  const handleSearchClick = () => {
    setShowAllSuggestions(true);
  };

  const handleSuggestionClick = (item) => {
    handleChange([...tags, item]);
    setTerm("");
    setShowAllSuggestions(false);
  };

  const handleBlur = () => {
    setShowAllSuggestions(false);
  };

  const handleSuggestionMouseDown = (e) => {
    e.preventDefault();
    handleSuggestionClick(e.target.textContent);
  };

  return (
    <div className="relative w-full search-box flex-grow-1 position-relative">
      <style jsx="true">{`
        .search-box {
          min-width: 16vw;
        }
        .input-container {
          display: flex;
          align-items: center;
          width: 100%;
          border: 1px solid #191e3c;
          border-radius: 0px;
          padding: 2px 5px;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          margin-left: 5px;
        }
        .input-container::-webkit-scrollbar {
          display: none;
        }
        .tag {
          background-color: #f3f4f6;
          border-radius: 4px;
          color: #374151;
          padding: 2px 6px;
          margin-right: 4px;
          font-size: 0.875rem;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
        }
        .tag-remove {
          margin-left: 4px;
          color: #6b7280;
          cursor: pointer;
          font-size: 1.2rem;
          line-height: 1;
        }
        .tag-input {
          flex-grow: 1;
          border: none;
          outline: none;
          font-size: 14px;
          padding: 7px 0;
          min-width: 50px;
        }
        .tag-input::placeholder {
          font-size: 0.85rem;
        }
        .checkbox {
          width: 20px;
          height: 20px;
          border: 2px solid #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          position: relative;
          margin-left: 8px;
          flex-shrink: 0;
        }
        .checkbox:checked {
          background-color: #191e3c;
          border-color: #191e3c;
        }
        .checkbox:checked::after {
          content: "✓";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 14px;
        }
        .save-icon {
          cursor: pointer;
          margin-left: 8px;
          flex-shrink: 0;
        }
        .suggestions {
          position: absolute;
          background-color: white;
          border: 1px solid #ccc;
          max-height: 150px;
          overflow-y: auto;
          z-index: 99999;
          border-radius: 4px;
          top: 38px;
          left: 0px;
        }
        .suggestion-item {
          padding: 4px;
          cursor: pointer;
        }
        .suggestion-item:hover {
          background-color: #f0f0f0;
        }
      `}</style>
      <div
        className="input-container gap-1 h-100 px-2 py-0 overflowx-auto"
        ref={containerRef}
      >
        {tags.length === 0 && (
          <IoIosSearch className="text-gray-400 mr-2 flex-shrink-0" />
        )}
        {tags.map((tag, index) => (
          <span key={index} className="tag flex-shrink-0">
            {tag}
            <span className="tag-remove" onClick={() => removeTag(index)}>
              &times;
            </span>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="tag-input"
          placeholder="Search"
          onKeyDown={handleKeyDown}
          onChange={(e) => setTerm(e.target.value)}
          onClick={handleSearchClick}
          onBlur={handleBlur}
        />

        <div
          className="d-flex align-items-center h-100 theme-border-left position-sticky end-0"
          title="Apply Thesaurus"
        >
          <input
            type="checkbox"
            className="checkbox height-16 width-16 "
            checked={isChecked}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>
      {/* <div className="suggestions w-100">
            {searchHistory.map((item, index) => (
              <div
                key={index}
                className="suggestion-item"
                onMouseDown={handleSuggestionMouseDown}
              >
                {item}
              </div>
            ))}
          </div> */}
      {showAllSuggestions && searchHistory.length !== 0 && (
        <div className="suggestions w-100">
          {searchHistory.map((item, index) => (
            <div
              key={index}
              className="suggestion-item"
              onMouseDown={handleSuggestionMouseDown}
            >
              {item}
            </div>
          ))}
        </div>
      )}
      <SaveSearchModal
        show={showModal}
        onHide={() => setShowModal(false)}
        searchName={searchName}
        setSearchName={setSearchName}
        onSave={handleSave}
      />
    </div>
  );
}
